<template>
  <v-app v-if="isFetched">
    <template v-if="!!this.$route.name">
      <template
        v-if="
          this.$route.name !== 'Login' &&
          this.$route.name !== 'SignUp' &&
          this.$route.name !== 'Upgrade' &&
          this.$route.name !== 'Welcome' &&
          this.$route.name !== 'Checkout' &&
          this.$route.name !== 'FirstLoading' &&
          this.$route.name !== 'ConfirmEmailChange'
        "
      >
        <the-navigation-drawer />
      </template>
      <account-warning v-if="isFullNoticeRoute" />
      <the-toolbar
        v-if="isMobileBreakpoint && this.$route.name !== 'Welcome'"
      />
    </template>

    <v-main>
      <!-- notice -->
      <account-warning v-if="isSmallNoticeRoute" />

      <!-- only not logged views -->
      <template v-if="isNotLogged">
        <v-container class="fill-height ma-0 pa-0" fluid>
          <router-view />
        </v-container>
      </template>

      <!-- normal views -->
      <template v-else>
        <router-view />
      </template>

      <!-- others -->
      <aside style="position: absolute">
        <modal-reset-billing-cycle />
        <move-modal
          v-if="this.$route.name !== 'Videos' && this.$route.name !== 'Folders'"
        />
        <video-delete-confirm
          v-if="this.$route.name !== 'Videos' && this.$route.name !== 'Folders'"
        />
        <folder-delete />
        <video-delete />
        <delete-assist-modal />
        <rename-panda-assist-modal />
        <confirm-cancel-upload />
        <checkout />
        <upgrade />
        <refer-disable-confirm />
        <import-modal :open="openImportModal" :uppyConfig="uppy" />
        <AddSubtitleModal />
        <AddDubbingModal />
        <AiCtaModal />
        <CreateVideoModal
          :open="openCreateVideoModal"
          :uppyConfig="uppy"
          :currentFolderId="currentFolderId"
          @close="
            openCreateVideoModal = false
            $root.$emit('resetIsAnyOtherModal')
          "
        />
        <usage-limit-modal />
        <upload-list
          v-if="uploadFiles.length"
          :files="uploadFiles"
          :handleCancel="handleCancel"
          :percent="uploadProgress"
          :handlePause="handlePause"
          :paused="uploadPaused"
          :handleResume="handleResume"
        />
        <snackbar />
        <BaseModalMessage v-if="globalBaseModalMessage.open" />
        <onboarding-modal />
        <checkout-done-modal />
        <no-subscription-modal v-if="this.$route.name !== 'Cart'" />
        <global-loading
          :value="globalLoadingValue"
          :open="globalLoadingOpen"
          :text="globalLoadingText"
        />
        <modal-item-logs />
        <warning-payment-issue />
        <credits-buy-modal />
      </aside>
    </v-main>
  </v-app>
</template>

<script>
// external imports
import { mapMutations, mapState, mapGetters } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import Uppy from '@uppy/core'
import tus from '@uppy/tus'
import Dashboard from '@uppy/dashboard'
import GoogleDrive from '@uppy/google-drive'
import Dropbox from '@uppy/dropbox'
import Onedrive from '@uppy/onedrive'
import Url from '@uppy/url'
import Portuguese from '@uppy/locales/lib/pt_BR'
import English from '@uppy/locales/lib/en_US'
import Dexie from 'dexie'
import intro_js from 'intro.js'

// internal imports
import { BACKUP_UPLOADER_URL, ENV_CONFIG } from './config/envs'
import Storage from '@/utils/Storage'
import Vimeo from '@/utils/Vimeo'
import store from '@/store'
import i18n from '@/translations/lang'

// API
import hostsApi from '@/services/hostsApi'
import organizationApi from '@/services/organizationApi'

// Components
import ModalResetBillingCycle from '@/components/ModalResetBillingCycle.vue'
import TheNavigationDrawer from '@/components/TheNavigationDrawer.vue'
import ImportModal from '@/components/ImportModal.vue'
import AiCtaModal from '@/components/AiCtaModal.vue'
import TheToolbar from '@/components/TheToolbar.vue'
import Snackbar from '@/components/Snackbar.vue'
import UsageLimitModal from '@/components/UsageLimitModal.vue'
import ConfirmCancelUpload from '@/components/ConfirmCancelUpload.vue'
import CheckoutDoneModal from '@/components/CheckoutDoneModal.vue'
import NoSubscriptionModal from '@/components/NoSubscriptionModal.vue'
import MoveModal from '@/components/MoveModal.vue'
import GlobalLoading from '@/components/GlobalLoading.vue'
import ModalItemLogs from '@/components/ModalItemLogs.vue'
import AccountWarning from '@/components/AccountWarnings/AccountWarning.vue'
import UploadList from '@/views/Videos/NewVideo/UploadList.vue'
import FolderDelete from '@/views/Folders/FolderDelete.vue'
import Checkout from '@/views/Checkout'
import Upgrade from '@/views/Upgrade'
import VideoDelete from '@/views/Videos/VideoDelete.vue'
import DeleteAssistModal from '@/views/PandaAssist/modals/deleteAssistModal.vue'
import RenamePandaAssistModal from '@/views/PandaAssist/modals/renamePandaAssistModal.vue'
import VideoDeleteConfirm from '@/views/Videos/VideoDeleteConfirm.vue'
import CreateVideoModal from '@/components/CreateVideoModal.vue'
import referDisableConfirm from '@/views/Domain/referDisableConfirm.vue'
import AddSubtitleModal from './components/AddSubtitleModal/AddSubtitleModal.vue'
import AddDubbingModal from './components/AddDubbingModal.vue'
import WarningPaymentIssue from './components/WarningPaymentIssue.vue'
import CreditsBuyModal from './components/CreditsBuyModal.vue'

// css
import 'vue-plyr/dist/vue-plyr.css'
import 'intro.js/minified/introjs.min.css'

import AbTestIcon from '@/assets/panda_abtest_icon.svg'
import PandaAssist from '@/assets/panda_assistAi_icon.svg'
import AnalyticsIcon from '@/assets/panda_analytics_icon.svg'
import ConfigIcon from '@/assets/panda_config_icon.svg'
import VideosIcon from '@/assets/panda_videos_icon.svg'
import LiveIcon from '@/assets/panda_live_icon.svg'
import NewsIcon from '@/assets/panda_news_icon.svg'
import SafetyIcon from '@/assets/panda_safety_icon.svg'
import PlayerIcon from '@/assets/panda_player_icon.svg'

export default {
  name: 'App',
  components: {
    AddDubbingModal,
    AddSubtitleModal,
    TheToolbar,
    TheNavigationDrawer,
    AiCtaModal,
    CreateVideoModal,
    AccountWarning,
    Snackbar,
    BaseModalMessage: () => import('@/components/BaseModalMessage.vue'),
    ModalResetBillingCycle,
    UploadList,
    FolderDelete,
    ImportModal,
    UsageLimitModal,
    ConfirmCancelUpload,
    Checkout,
    VideoDelete,
    Upgrade,
    OnboardingModal: () => import('./components/OnboardingModal.vue'),
    CheckoutDoneModal,
    NoSubscriptionModal,
    MoveModal,
    VideoDeleteConfirm,
    GlobalLoading,
    ModalItemLogs,
    referDisableConfirm,
    WarningPaymentIssue,
    CreditsBuyModal,
    DeleteAssistModal,
    RenamePandaAssistModal,
  },
  data: () => ({
    isFetchingPlanInfo: false,
    uploadServersTimeout: null,
    openCreateVideoModal: false,
    hideAccountWarning: true,
    openImportModal: false,
    uploadPaused: false,
    uploadProgress: 0,
    uploadOpened: false,
    uploadFiles: [],
    toBeUploaded: [],
    isFetched: false,
    tusEndpoint: '',
    uppyUpdated: 0,
    globalLoadingValue: 200,
    globalLoadingOpen: false,
    globalLoadingText: '',
    uploaderArray: [],
    rebaseUploaderArray: [],
    uppyLocale: Portuguese,
    windowWidth: window.innerWidth,
    userPerms: [],
    isBeta: false,
    defaultDelay: 2000,
    newLive: Storage.getItem('newLive'),
  }),
  computed: {
    ...mapGetters('user', ['canSeePlanInfo']),
    ...mapState({
      userInfo: (state) => state.user.userInfo,
      planInfo: (state) => state.user.userPlanInfo,
      globalBaseModalMessage: (state) => state.user.globalBaseModalMessage,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
      userPermissions: (state) => state.user.userPermissions,
    }),
    currentFolderId() {
      if (this.$route.name === 'Folders') {
        return this.$route.params.id
      }
      return false
    },
    isRootUser() {
      return this.$store.state.user?.userAllInfo?.is_root ?? false
    },
    isFullNoticeRoute() {
      if (
        (this.$route.name !== 'Login' &&
          this.$route.name !== 'SignUp' &&
          this.$route.name !== 'FirstLoading' &&
          !this.hideAccountWarning &&
          (this.$route.name === 'Cart' ||
            this.$route.name === 'Upgrade' ||
            this.$route.name === 'Checkout')) ||
        this.isMobileBreakpoint
      ) {
        return true
      }
      return false
    },
    isSmallNoticeRoute() {
      if (
        this.$route.name !== 'Login' &&
        this.$route.name !== 'SignUp' &&
        this.$route.name !== 'FirstLoading' &&
        this.$route.name !== 'Cart' &&
        this.$route.name !== 'Upgrade' &&
        this.$route.name !== 'Checkout' &&
        !this.hideAccountWarning &&
        !this.isMobileBreakpoint
      ) {
        return true
      }
      return false
    },
    uppy() {
      this.uppyUpdated
      const objTus = this.handleTusObject()
      if (!this.isChrome() || this.isMobile()) {
        objTus.resume = false
        objTus.parallelUploads = 1
      }
      let objCompanion = this.handleCompanionObj()
      const instanceUppy = new Uppy({
        autoProceed: true,
        allowMultipleUploads: true,
        restrictions: {
          allowedFileTypes: ['video/*', '.ts'],
        },
        locale: this.uppyLocale,
      })
        .use(tus, objTus)
        .use(Dashboard)
        .use(Vimeo, { ...objCompanion, id: 'Vimeo' })
        .use(Url, { ...objCompanion, id: 'url' })
        .use(GoogleDrive, { ...objCompanion, id: 'google-drive' })
        .use(Dropbox, { ...objCompanion, id: 'dropbox' })
        .use(Onedrive, { ...objCompanion, id: 'onedrive' })
      return instanceUppy
    },
    introJS() {
      return intro_js
    },
    isNotLogged() {
      const routes = ['Login', 'SignUp', 'ConfirmEmailChange']
      return routes.includes(this.$route.name)
    },
  },
  provide() {
    return {
      intro_js: this.introJS,
    }
  },
  async created() {
    this.isBeta = location.search.match('beta')
    this.setUppyEvents()
    this.setTheme()
    let params = new URLSearchParams(location.search)
    let accessType = params.get('type')
    if (accessType === 'first-access' || accessType === 'recover-pass') {
      await this.handleLogout()
    }
    let buyCredits = location.search.match('buyCredits')
    if (buyCredits) {
      this.UPDATE_CREDITS_TOGGLE(true)
    }
    await this.userRequests()
    window.addEventListener('beforeunload', (event) => {
      if (this.uploadFiles.length > 0) {
        event.preventDefault = true
        event.returnValue = 'Deseja realmente sair? Arquivos serão perdidos!'
      }
    })
  },
  watch: {
    value() {
      this.uppyUpdated++
    },
    $route: {
      handler() {
        if (this.userPermissions.length) this.confirmRoutePermission()
        this.setPermsInfo()
      },
      deep: true,
    },
    userPermissions: {
      handler() {
        this.confirmRoutePermission()
      },
      deep: true,
    },
  },
  mounted() {
    this.onResize()
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })
    this.setUppyLanguage(this.getLocale())
    this.uppyUpdated++
    this.$root.$on('logout', () => {
      this.handleLogout()
    })
    this.$root.$on('readUploadServers', () => {
      this.readUploadServers()
    })
    this.isFetched = true
    this.$root.$on('openCreateVideoModal', () => {
      this.openCreateVideoModal = true
      this.$root.$emit('resetIsAnyOtherModal', true)
    })
    this.$root.$on('showAccountWarning', () => {
      this.hideAccountWarning = false
    })
    this.$root.$on('goToDuplicatedVideo', (id) => {
      this.$router.push({ name: 'Videos' })
      this.$root.$emit('changeNavItem', 0)
      setTimeout(() => {
        this.$router.push({ name: 'VideoPage', params: { id } })
      }, 100)
    })
    this.$root.$on('initDexie', () => {
      window.db = new Dexie(
        `panda-${this.$store.state.user.userInfo?.sub ?? this.getSubKey()}`
      )
      window.db.version(4).stores({
        videos: '++id, video_external_id, folder_id, status', // Primary key and indexed props
        AI_batch_queue: '++id, video_id, status',
      })
    })
    this.$root.$on('checkIfHasUploaderUrl', async () => {
      const uploaderUrl = Storage.getItem('uploaderUrlv3')
      if (uploaderUrl) {
        return
      }
      this.setUppyEventsOff()
      await this.setUserUploader()
      this.uppyUpdated++
      setTimeout(() => {
        this.setUppyEvents()
      }, 1000)
    })
    this.$root.$on('uppyChangeHost', (newServer) => {
      this.setUppyEventsOff()
      if (newServer === 'default') {
        this.setUserUploader()
      } else {
        Storage.setItem(
          'uploaderUrlv3',
          `https://${newServer}.pandavideo.com.br`
        )
      }
      this.uppyUpdated++
      setTimeout(() => {
        this.setUppyEvents()
      }, 1000)
    })
    this.$root.$on('handleLoginRoute', () => {
      this.uppy.reset()
      this.hideAccountWarning = true
    })
    this.$root.$on('importModal', async (data) => {
      this.openImportModal = data.open
      const session = await store().dispatch('session/refreshSession')
      const token = session.accessToken.jwtToken
      if (token) {
        Storage.setItem('accessToken', session.accessToken.jwtToken)
      }
      this.UPDATE_REFRESH_TOKEN(session.refreshToken.token)
    })
    this.$root.$on('removeFromQueue', (file) => {
      this.uppy.removeFile(file.id)
    })
    this.$root.$on('addVideo', async (data) => {
      let storageKeys = Storage.getStorageKeys()
      for (let key of storageKeys) {
        if (key[0].includes('tus::tus-uppy')) {
          Storage.removeItem(key[0])
        }
      }
      const addedFile = data.file
      const session = await store().dispatch('session/refreshSession')
      const token = session.accessToken.jwtToken
      if (token) {
        Storage.setItem('accessToken', session.accessToken.jwtToken)
      }
      let id = ''
      if (data.folderId) {
        id = data.folderId
      } else {
        id = this.$route.params.id
      }
      const userId = this.$store.getters['session/userSub']
      const refreshToken = session.refreshToken.token
      const authorization = 'Bearer' + Storage.getItem('accessToken')
      try {
        this.uppy.addFile({
          source: 'file input',
          name: addedFile.name,
          type: addedFile.type,
          data: addedFile,
          meta: {
            folder_id: id,
            user_id: userId,
            refresh_token: refreshToken,
            authorization: authorization,
          },
        })
      } catch (e) {
        if (e.isRestriction && e.message.match(/duplicate/i)) {
          const existingFile = this.uppy
            .getFiles()
            .find((searchFile) => searchFile.name === addedFile.name)
          if (existingFile) {
            const fileName = addedFile.name
            const lastCharacters = fileName.substring(fileName.length - 3)
            const regex = /\([0-9]+\)/
            const regexTest = regex.test(lastCharacters)
            let fileNumber = fileName.slice(-2).charAt(0)
            if (regexTest) {
              const newFileNumber = Number(fileNumber) + 1
              Object.defineProperty(addedFile, 'name', {
                writable: true,
                value: addedFile.name.slice(0, -3) + ` (${newFileNumber})`,
              })
              this.$root.$emit('addVideo', { file: addedFile })
            } else {
              Object.defineProperty(addedFile, 'name', {
                writable: true,
                value: addedFile.name + ' (1)',
              })
              this.$root.$emit('addVideo', { file: addedFile })
            }
          }
        } else {
          this.UPDATE_GLOBAL_SNACKBAR({ open: true, text: `${e}` })
        }
      }
      this.uploadPaused = false
    })
    this.$root.$on('setUserInfo', () => this.requestUserInfo())
    this.$root.$on('updateAllUserInfo', () => this.setUserInfo())
    this.$root.$on('setGlobalLoading', (data) => {
      this.globalLoadingOpen = data.open
      this.globalLoadingText = data.text
    })
    this.$root.$on('setUppyDashboardLanguage', (lang) => {
      this.setUppyLanguage(lang)
    })
    this.$root.$on('updateNavigationItems', () => {
      this.setPermsInfo()
    })
    this.$root.$on('loginUserAppVue', async () => {
      await this.userRequests()
    })
  },
  beforeDestroy() {
    this.$root.$off('addVideo')
    this.$root.$off('importModal')
    this.$root.$off('removeFromQueue')
    this.$root.$off('uppyChangeHost')
    this.$root.$off('checkIfHasUploaderUrl')
    this.$root.$off('setUserInfo')
    this.$root.$off('updateAllUserInfo')
    this.$root.$off('setGlobalLoading')
    window.clearTimeout(this.uploadServersTimeout)
    window.removeEventListener('beforeunload', this.beforeWindowUnload)
    window.removeEventListener('resize', this.onResize)
    this.uppy.close()
  },
  methods: {
    ...mapMutations('user', [
      'UPDATE_USER_INFO',
      'UPDATE_GLOBAL_SNACKBAR',
      'UPDATE_REFRESH_TOKEN',
      'UPDATE_USER_PLAN_INFO',
      'UPDATE_USER_ALLINFO',
      'UPDATE_USER_PERMISSIONS',
      'UPDATE_NAV_ITEMS',
      'UPDATE_FETCH_PERMISSIONS',
      'UPDATE_SETTINGS_NAV_ITEMS',
      'UPDATE_SECURITY_NAV_ITEMS',
      'UPDATE_IS_MOBILE_BREAKPOINT',
      'UPDATE_WINDOW_INFO',
      'UPDATE_CREDITS_TOGGLE',
    ]),
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    },
    async userRequests() {
      await this.requestUserInfo()
      if (this.userInfo?.sub) {
        await this.setUserInfo(this.defaultDelay)
      }
    },
    setPermsInfo() {
      const i18n = this.$i18next
      let navItems = [
        {
          text: this.$i18next.t('message.videos'),
          icon: VideosIcon,
          route: 'Videos',
        },
        {
          text: this.$i18next.t('message.player'),
          icon: PlayerIcon,
          route: 'Player',
        },
        {
          text: this.$i18next.t('message.settings'),
          icon: ConfigIcon,
          route: 'UserPlan',
        },
        {
          text: this.$i18next.t('message.pages.toolbar.news'),
          icon: NewsIcon,
          route: 'news',
        },
      ]
      if (
        this.userPerms.includes('get-video') &&
        this.userPerms.includes('get-analytics')
      ) {
        navItems.splice(2, 0, {
          text: 'Analytics',
          icon: AnalyticsIcon,
          route: 'AnalyticsV2',
        })
      }
      if (this.userPerms.includes('get-test-a-b')) {
        navItems.splice(2, 0, {
          text: i18n.t('message.pages.AbTest.AbTest'),
          icon: AbTestIcon,
          route: 'AbTest',
        })
      }
      if (this.userPerms.includes('get-test-a-b')) {
        navItems.splice(2, 0, {
          text: 'PandaAssist AI',
          icon: PandaAssist,
          route: this.isBeta ? 'PandaAssist' : 'ShortlyPandaAssistAI',
        })
      }
      if (this.userPerms.includes('get-live')) {
        navItems.splice(2, 0, {
          text: 'Live',
          icon: LiveIcon,
          route: Storage.getItem('oldLive') ? 'Live' : 'LiveV2',
        })
      }

      const securityPerms = ['get-general-config', 'get-watermark']
      if (securityPerms.some((perm) => this.userPerms.includes(perm))) {
        const addItem = ({ title, name, addBetaTag = false }) => {
          securityNavItems.push({ title, to: { name }, addBetaTag })
          if (!route) route = name
        }
        const securityNavItems = []
        let route

        if (this.userPerms.includes('get-general-config')) {
          addItem({
            title: this.$i18next.t(
              'message.pages.folderSettings.allowedDomains'
            ),
            name: 'Domains',
          })
        }
        if (this.userPerms.includes('get-watermark')) {
          addItem({
            title: this.$i18next.t('message.pages.drm.watermark'),
            name: 'DRM',
          })
        }
        if (this.userPerms.includes('get-general-config')) {
          addItem({
            title: this.$i18next.t('message.routesTitles.AntiDownload'),
            name: 'AntiDownload',
            addBetaTag: true,
          })
        }

        navItems.splice(2, 0, {
          text: this.$i18next.t('message.safety'),
          icon: SafetyIcon,
          route,
        })

        this.UPDATE_SECURITY_NAV_ITEMS(securityNavItems)
      }

      this.UPDATE_NAV_ITEMS(JSON.parse(JSON.stringify(navItems)))
      let settingsNavItems = [
        {
          title: i18n.t('message.pages.advanced.routeTitle'),
          to: '/settings/advanced',
        },
        {
          title: 'Webhooks',
          to: '/settings/webhooks',
        },
      ]
      if (this.canSeePlanInfo) {
        settingsNavItems.splice(1, 0, {
          title: i18n.t('message.pages.credits.title'),
          to: '/settings/credits',
        })
        settingsNavItems.splice(0, 0, {
          title: i18n.t('message.plan'),
          to: '/settings/plan',
        })
      }
      if (this.userPerms.includes('get-general-config')) {
        settingsNavItems.splice(3, 0, {
          title: i18n.t('message.pages.qualitySettings.title'),
          to: '/settings/quality',
        })
      }
      if (this.isRootUser) {
        settingsNavItems.splice(3, 0, {
          title: i18n.t('message.pages.integrations.title'),
          to: '/settings/integrations',
        })
      }
      if (this.userPerms.includes('get-user')) {
        settingsNavItems.splice(3, 0, {
          title: i18n.t('message.routesTitles.Users'),
          to: '/settings/management',
        })
      }
      if (this.userPerms.includes('get-organization')) {
        settingsNavItems.splice(1, 0, {
          title: i18n.t('message.routesTitles.Account'),
          to: '/settings/organization',
        })
      }
      this.UPDATE_SETTINGS_NAV_ITEMS(settingsNavItems)
      this.UPDATE_FETCH_PERMISSIONS(true)
    },
    onResize() {
      this.windowWidth = window.innerWidth
      this.UPDATE_WINDOW_INFO(this.windowWidth)
    },
    getSubKey() {
      let subKey = null
      let keys = Storage.getStorageKeys()
      for (let key of keys) {
        if (key[0].includes('LastAuthUser')) {
          subKey = key[1]
        }
      }
      return subKey
    },
    setTheme() {
      const selectedTheme = Storage.getItem('selectedThemeV2')

      if (selectedTheme) {
        if (selectedTheme === 'dark') {
          document.body.classList.remove('scrollbar-light-mode')
          document.body.classList.add('scrollbar-dark-mode')
          document.body.classList.add('bg-dark')
          this.$vuetify.theme.dark = true
        } else {
          document.body.classList.add('scrollbar-light-mode')
          document.body.classList.remove('scrollbar-dark-mode')
          document.body.classList.remove('bg-dark')
          this.$vuetify.theme.dark = false
        }
      } else {
        const darkThemeMq = window.matchMedia('(prefers-color-scheme: dark)')
        if (darkThemeMq?.matches) {
          document.body.classList.remove('scrollbar-light-mode')
          document.body.classList.add('scrollbar-dark-mode')
          document.body.classList.add('bg-dark')
          this.$vuetify.theme.dark = true
          Storage.setItem('selectedThemeV2', 'dark')
        } else {
          document.body.classList.add('scrollbar-light-mode')
          document.body.classList.remove('scrollbar-dark-mode')
          document.body.classList.remove('bg-dark')
          this.$vuetify.theme.dark = false
          Storage.setItem('selectedThemeV2', 'light')
        }
      }
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      return 'pt-BR'
    },
    setUppyLanguage(lang) {
      if (lang.includes('en')) {
        this.uppyLocale = English
      } else {
        this.uppyLocale = Portuguese
      }
      this.setUppyEventsOff()
      this.uppyUpdated++
      setTimeout(() => {
        this.setUppyEvents()
      }, 1000)
    },
    setUppyEventsOff() {
      this.uppy.off('file-added')
      this.uppy.off('upload-success')
      this.uppy.off('progress')
      this.uppy.off('restriction-failed')
      this.uppy.off('back-online')
      this.uppy.off('upload-error')
    },
    setUppyEvents() {
      // eslint-disable-next-line no-unused-vars
      this.uppy.on('upload-error', async (file, error, response) => {
        try {
          let err = error.toString()
          if (err.match('reached the maximum allowed')) {
            this.$root.$emit('onStorageSizeMax')
            this.$root.$emit('importUploadError')
            this.$root.$emit('forcePauseUploadList')
            if (!this.isFetchingPlanInfo) {
              const _this = this
              this.$root.$emit('fixUserLimit', true)
              this.isFetchingPlanInfo = true
              this.$root.$on('loadedUserLimitToUppy', () => {
                const type = err.includes('storage') ? 'storage' : 'traffic'
                _this.isFetchingPlanInfo = false

                if (_this.planInfo.subscription.status !== 'trialing') {
                  _this.$root.$emit('usageLimitModal', {
                    open: true,
                    type,
                    storagePlan: _this.planInfo?.limit?.storage_plan,
                  })
                } else if (_this.planInfo.subscription.status === 'trialing') {
                  _this.$root.$emit('ModalResetBillingCycle')
                }
              })
            }
          } else if (
            err.match('no space left') ||
            err.match('endpoint might be blocked')
          ) {
            this.swapUploadServer(file)
          }
        } catch (e) {
          this.UPDATE_GLOBAL_SNACKBAR({ open: true, text: `${e}` })
        }
      })
      this.uppy.on('file-added', (file) => {
        let storageKeys = Storage.getStorageKeys()
        for (let key of storageKeys) {
          if (key[0].includes('tus::tus-uppy')) {
            Storage.removeItem(key[0])
          }
        }
        //Set dos metadados em vídeos importados de outra  plataforma
        let id = ''
        if (file.meta.folder_id) {
          id = file.meta.folder_id
        } else {
          id = this.$route.params.id
        }
        const userId = this.$store.getters['session/userSub']
        const refreshToken = this.$store.state.user.refreshToken
        const authorization = 'Bearer ' + Storage.getItem('accessToken')
        this.uppy.setFileMeta(file.id, {
          video_id: uuidv4(),
          folder_id: id,
          user_id: userId,
          refresh_token: refreshToken,
          authorization: authorization,
        })
        this.uploadFiles = this.uppy.getFiles()
        this.uploadOpened = true
        if (this.uploadPaused) {
          this.uppy.resumeAll()
        }
      })
      this.uppy.on('upload-success', (file) => {
        this.$root.$emit('fixUserLimit')
        const videoFormat = {
          title: file.name,
          id: file.meta.video_id,
          status: 'DRAFT',
          folder_id: file.meta.folder_id,
          updated_at: new Date().toISOString(),
        }
        this.uploadFiles = this.uppy.getFiles()
        this.$root.$emit('addVideoVideosPage', videoFormat)
        let storagedVideosArray = Storage.getItem(
          `panda-queue-${this.$store.state.user.userInfo.sub}`
        )
        if (!storagedVideosArray) {
          Storage.setItem(
            `panda-queue-${this.$store.state.user.userInfo.sub}`,
            [videoFormat]
          )
        } else {
          storagedVideosArray.push(videoFormat)
          Storage.setItem(
            `panda-queue-${this.$store.state.user.userInfo.sub}`,
            storagedVideosArray
          )
        }
      })
      this.uppy.on('progress', (progress) => {
        this.uploadProgress = progress
        this.uploadFiles = this.uppy.getFiles()
      })
      this.uppy.on('restriction-failed', (file) => {
        if (file.extension === 'pdf') {
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text: i18n.t('message.fileExtensionError'),
          })
        }
      })
      this.uppy.on('back-online', () => {
        this.uppy.retryAll()
      })
    },
    async readUploadServers() {
      if (
        this.$route.name &&
        this.$route.name !== 'Login' &&
        this.$route.name !== 'SignUp'
      ) {
        try {
          const ret = await hostsApi.getHosts()
          let hostsArray = []
          for (let hostArea in ret.data.hosts) {
            for (let i = 0; i < hostArea.length; i++) {
              let host = ret.data.hosts[hostArea][i]
              if (host) {
                hostsArray.push(host)
              }
            }
          }
          this.uploaderArray = hostsArray
          this.rebaseUploaderArray = hostsArray
        } catch (e) {
          if (e?.response?.status === 401) {
            this.handleLogout()
          } else {
            this.uploadServersTimeout = setTimeout(() => {
              this.readUploadServers()
            }, 2500)
          }
          console.error(e)
        }
      } else {
        this.uploadServersTimeout = setTimeout(() => {
          this.readUploadServers()
        }, 2500)
      }
    },
    async swapUploadServer(file) {
      const uploaderUrl = Storage.getItem('uploaderUrlv3')
      this.uploaderArray = this.uploaderArray.filter(
        (server) => server != uploaderUrl
      )
      if (this.uploaderArray.length) {
        const randomHost =
          this.uploaderArray[
            Math.floor(Math.random() * this.uploaderArray.length)
          ]
        this.$root.$emit('uppyChangeHost', randomHost)
        setTimeout(() => {
          this.uppy.addFile(file)
        })
      } else {
        this.uploaderArray = this.rebaseUploaderArray
        this.swapUploadServer()
        this.$root.$emit('uploadErrorModal', { open: true })
        this.$root.$emit('resetIsAnyOtherModal', true)
      }
    },
    async setUserUploader() {
      try {
        const ret = await hostsApi.getHosts()
        let hostsArray = []
        for (let hostArea in ret.data.hosts) {
          for (let i = 0; i < hostArea.length; i++) {
            let host = ret.data.hosts[hostArea][i]
            if (host) {
              hostsArray.push(host)
            }
          }
        }
        const randomHost =
          hostsArray[Math.floor(Math.random() * hostsArray.length)]
        Storage.setItem(
          'uploaderUrlv3',
          `https://${randomHost}.pandavideo.com.br`
        )
        setTimeout(() => {
          this.uppyUpdated++
        }, 300)
      } catch (e) {
        Storage.setItem('uploaderUrlv3', BACKUP_UPLOADER_URL)
        this.uppyUpdated++
        console.error(e)
      }
      this.uppyUpdated++
    },
    async requestUserInfo() {
      try {
        const session = await this.$store.dispatch('session/fetchSession')
        const isLogged = !!session
        if (isLogged) {
          Storage.setItem('accessToken', session.accessToken.jwtToken)
          try {
            this.UPDATE_USER_INFO(this.$store.getters['session/userAttributes'])
          } catch (e) {
            this.handleLogout()
          }
        }
      } catch (error) {
        if (error === 'No current user') return
        this.UPDATE_GLOBAL_SNACKBAR({ open: true, text: `${error}` })
      }
    },
    async setUserInfo(delay) {
      try {
        let ret = await organizationApi.getUserById(this.userInfo.sub)
        this.UPDATE_USER_ALLINFO({ ...ret.data })
        await this.setUserPermissions(this.defaultDelay)
        this.$root.$emit('emitCreateChangelogfy')
      } catch (e) {
        delay = Math.min(delay * 2, 60000)
        setTimeout(() => {
          this.setUserInfo(delay)
        }, delay)
      }
    },
    async setUserPermissions(delay) {
      try {
        let ret = await organizationApi.getRolePermissionsById(
          this.$store.state.user.userAllInfo.role_id
        )
        let permissions = ret.data.permissions
        const mappedPermissions = permissions.map(
          (permission) => permission.name
        )
        this.userPerms = mappedPermissions
        this.UPDATE_USER_PERMISSIONS(mappedPermissions)
        this.setPermsInfo()
      } catch (e) {
        delay = Math.min(delay * 2, 60000)
        setTimeout(() => {
          this.setUserPermissions(delay)
        }, delay)
      }
    },
    confirmRoutePermission() {
      const permissibleRoutes = [
        'Live',
        'NewLive',
        'NewLiveV2',
        'DRM',
        'Management',
        'AbTest',
      ]
      const routePermissions = {
        Live: 'get-live',
        NewLive: 'get-live',
        NewLiveV2: 'get-live',
        DRM: 'get-watermark',
        Management: 'get-user',
        AbTest: 'get-test-a-b',
        Domains: 'get-general-config',
        Quality: 'get-general-config',
      }
      const { name } = this.$route
      const userPermissions = [...this.userPermissions]

      const requiredPermission = routePermissions[name]

      if (permissibleRoutes.includes(name)) {
        if (
          requiredPermission &&
          !userPermissions.includes(requiredPermission)
        ) {
          this.$router.push({ name: 'Videos' })
          this.$nextTick(() => {
            this.$root.$emit('changeNavItem', 0)
          })
        }
      }
    },
    handleTusObject() {
      let uploaderUrl = `${BACKUP_UPLOADER_URL}/files/`
      if (ENV_CONFIG !== 'production') {
        uploaderUrl = `${BACKUP_UPLOADER_URL}:8443/files/`
      }
      const storageUrl = Storage.getItem('uploaderUrlv3')
      if (storageUrl) {
        uploaderUrl = `${storageUrl}/files/`
        if (ENV_CONFIG !== 'production') {
          uploaderUrl = `${storageUrl}:8443/files/`
        }
      }
      let objTus = {
        id: 'tus',
        endpoint: uploaderUrl,
        resume: true,
        retryDelays: [0, 1000],
        parallelUploads: 5,
        limit: 2,
      }
      if (!this.isChrome() || this.isMobile()) {
        objTus.resume = false
        objTus.parallelUploads = 1
      }
      return objTus
    },
    handleCompanionObj() {
      let companionUrl = `${BACKUP_UPLOADER_URL}:3020`
      if (ENV_CONFIG !== 'production') {
        companionUrl = `${BACKUP_UPLOADER_URL}:3021`
      }
      const storageUrl = Storage.getItem('uploaderUrlv3')
      if (storageUrl) {
        companionUrl = `${storageUrl}:3020`
        if (ENV_CONFIG !== 'production') {
          companionUrl = `${storageUrl}:3021`
        }
      }
      let objCompanion = {
        id: 'companion',
        target: Dashboard,
        companionUrl: companionUrl,
      }
      return objCompanion
    },
    handlePause() {
      this.uploadPaused = true
      this.uppy.pauseAll()
      this.uploadFiles = this.uppy.getFiles()
    },
    handleResume() {
      this.uploadPaused = false
      this.uppy.resumeAll()
      this.uploadFiles = this.uppy.getFiles()
    },
    handleCancel() {
      this.uppy.cancelAll()
      this.uploadFiles = this.uppy.getFiles()
    },
    async handleLogout() {
      try {
        this.setStorageConfig()
        await this.$store.dispatch('session/signOut')
        this.UPDATE_USER_PLAN_INFO(false)
        this.$router.push({ name: 'Login' })
        this.$crisp.push(['do', 'chat:hide'])
      } catch (e) {
        console.error(e)
      }
    },
    setStorageConfig() {
      let StorageData = Storage.getStorageKeys()
      Storage.clear()
      StorageData.map((key) => {
        const keyParameter = key[0]
        if (
          keyParameter === 'hasClosedAiBanner' ||
          keyParameter === 'introjs-trash-selected-finished' ||
          keyParameter === 'hasClosedTrainingBanner' ||
          keyParameter === 'introjs-trash-finished' ||
          keyParameter === 'trainingBannerExpiry'
        )
          Storage.setItem(keyParameter, true)
      })
    },
    isChrome() {
      return !!window.chrome
    },
  },
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700&display=swap');
@import '@/assets/scss/base_components.scss';
@import '@/assets/scss/_typography.scss';

$title-font: 'Rubik';
$body-font-family: 'Rubik';

.bgColor {
  background-color: var(--v-neutral-darken2);
}

.bg-dark {
  background-color: var(--v-neutral-darken2);

  .noFilesAddedText {
    color: #fff;
  }
}

.text-h7 {
  font-size: 18px;
}

.v-application {
  .iptMessage__error--icon {
    font-size: 14px;
    margin-left: -10px;
    transform: translateY(-1px);
  }

  font-family: $body-font-family, sans-serif !important;

  .title {
    font-family: $title-font, sans-serif !important;
  }

  .headline,
  .text-h1,
  .text-h2,
  .text-h3,
  .text-h4,
  .text-h5,
  .text-h6,
  .text-body-1,
  .text-body-2,
  .title,
  .text-subtitle-1,
  .text-subtitle-2,
  .text-caption,
  .subheading {
    font-family: $body-font-family !important;
  }
}

html {
  overflow-y: auto;

  * {
    font-family: $title-font !important;
  }
}

.virtualTable {
  $color_1: #25304a !important;
  $color_2: var(--v-neutral-darken2) !important;

  border-color: $color_1;

  .item-line {
    background-color: $color_2;

    &:hover {
      background-color: $color_2;
    }

    .item-cell {
      border-color: $color_1;
    }
  }

  .main-scroll {
    border-color: $color_1;
  }
}

.embed-textarea .v-text-field__slot {
  margin: 0 -47px 4px 0 !important;
}

.status-button {
  background-color: #d7d7d7;
  padding: 0px 4px;
  border-radius: 4px;
}

.v-divider {
  border-color: varFigma(neutral-95-lightest) !important;
}

.v-toolbar__content .v-btn:hover::before,
.v-toolbar__extension .v-btn:hover::before {
  opacity: 0 !important;
}

.completed--status-button {
  background-color: #d0d6fb;
  color: #0f22a1 !important;
}

.draft--status-button {
  background-color: red;
  color: #fff !important;
}

.inprogress--status-button {
  background-color: rgb(76, 175, 80);
  color: #fff !important;
}

.live-draft--status-button {
  background-color: #ffecb3;
  color: #3f404c !important;
}

.live-finished--status-button {
  background-color: #1867c0;
  color: #fff !important;
}

.noFilesAddedText {
  font-weight: 400;
  font-size: 1.125rem;
  line-height: 135%;
  text-align: center;
  color: #3f404c;
}

.f-width {
  width: 100% !important;
}

.pointerTable {
  cursor: pointer;
}

.pointer {
  cursor: pointer;
}

.text--error {
  font-size: 12px;
  color: var(--v-error-base);
}

.theme--dark.v-card {
  background-color: var(--v-neutral-darken2);
}

.bg-dark .acceptTerms .v-icon.v-icon {
  color: $neutral-80-lighter-dark;
}

.pointer {
  cursor: pointer;
}

.external-chip {
  background: var(--v-brand-lighten2) !important;
  font-weight: 500;
  height: 18px !important;
  margin-left: 6px;
}

.external-chip.no-hover.v-chip:hover::before {
  opacity: 0;
}

.external-chip * {
  color: var(--v-brand-darken2) !important;
  font-size: 12px;
}

.external-chip .v-icon {
  font-size: 18px;
}

.newItemText {
  color: var(--v-primary-base);
  font-weight: 300;
  text-transform: uppercase;

  &.ai {
    position: absolute;
    top: 4px;
    margin-left: 3px;
  }
}

.v-application
  .v-menu__content.menuable__content__active.v-autocomplete__content {
  margin-top: 4px !important;
  box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.15) !important;
  border-radius: 4px !important;
  max-width: 30vw;
  left: 0;
  right: 49px;
}
.v-application
  .v-menu__content.menuable__content__active.v-autocomplete__content
  .v-list-item {
  padding: 0;
  margin: 0 16px;
  border-radius: 4px;
}

.v-menu__content.menuable__content__active.v-autocomplete__content
  .v-list-item::before {
  border-radius: 4px;
  background-color: var(--v-primary-base);
}

.v-menu__content {
  border-radius: var(--spacing-01, 2px);
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.15);
  font-size: 12px;
  border: 1px solid varFigma(neutral-95-lightest);
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.theme--dark.v-list {
  color: var(--v-neutral-lighten4);
}

.v-list-item--dense .v-list-item__title,
.v-list-item--dense .v-list-item__subtitle,
.v-list--dense .v-list-item .v-list-item__title,
.v-list--dense .v-list-item .v-list-item__subtitle {
  color: varFigma(neutral-30-base) !important;
}
.v-dialog {
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  border: 1px solid varFigma(neutral-95-lightest);
}

.v-menu__content.menuable__content__active.v-autocomplete__content
  .v-list-item:hover::before {
  opacity: 0.16;
}

.text-subtitle-2 {
  &__standardized {
    line-height: 1.3rem;
    &--modal {
      line-height: 1.2rem;
    }
  }
}
::-webkit-scrollbar {
  width: 4px;
  border-radius: 8px;
}

::-webkit-scrollbar-thumb {
  border-radius: 8px;
}

/* Estilo para Modo Claro */
body.scrollbar-light-mode ::-webkit-scrollbar-thumb {
  background-color: #d0d1d9;
}

/* Estilo para Modo Escuro */
body.scrollbar-dark-mode ::-webkit-scrollbar-thumb {
  background-color: #52577a;
}
</style>
